import React from 'react';
import styled from 'styled-components';
import ExclamationTriangle from '@splunk/react-icons/ExclamationTriangle';
import InformationDiamond from '@splunk/react-icons/InformationDiamond';
import Circle from '@splunk/react-icons/Circle';
import Clickable from '@splunk/react-ui/Clickable';
import { variables } from '@splunk/themes';
import { _ } from '@splunk/ui-utils/i18n';

type iconColorType =
    | 'notificationColorNegative'
    | 'notificationColorInfo'
    | 'notificationColorCaution';

const StatusOk = styled(Circle).attrs(() => ({
    variant: 'filled',
    'aria-hidden': true,
}))``;

const WarningIcon = styled(InformationDiamond).attrs(() => ({
    variant: 'filled',
    'aria-hidden': true,
}))``;

const ErrorIcon = styled(ExclamationTriangle).attrs(() => ({
    variant: 'filled',
    'aria-hidden': true,
}))``;

const types = ['error', 'ok', 'warning'] as const;
export type IconStatus = (typeof types)[number];
const statusMap: Record<
    IconStatus,
    {
        icon: typeof ExclamationTriangle;
        color: iconColorType;
        label: string;
    }
> = {
    error: {
        icon: ErrorIcon,
        color: 'notificationColorNegative',
        label: _('Error'),
    },
    ok: {
        icon: StatusOk,
        color: 'notificationColorInfo',
        label: _('Status ok'),
    },
    warning: {
        icon: WarningIcon,
        color: 'notificationColorCaution',
        label: _('Warning'),
    },
};

interface ClickableStyleProps {
    height: number;
    width: number;
    iconSize: number;
    iconSizeOnHover: number;
    colorKey: iconColorType;
    clickable?: boolean;
}

const ClickableIcon = styled(Clickable)`
    display: flex;
    justify-content: center;
    align-items: center;
`;

interface IconContainerProps extends ClickableStyleProps {
    ariaLabel?: string;
}

const IconContainer = styled.div.attrs<IconContainerProps>(({ ariaLabel }) => ({
    'data-test': 'simple-status-icon-container',
    'aria-label': ariaLabel,
    role: ariaLabel ? 'img' : undefined,
}))<IconContainerProps>`
    height: ${({ height }) => height}px;
    width: ${({ width }) => width}px;
    // padding to increase the hover area on the icon to show tooltip
    padding: 0 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    svg {
        height: ${({ iconSize }) => iconSize}px;
        width: ${({ iconSize }) => iconSize}px;
    }

    // Increase SVG size when this component is hovered over or has keyboard focus
    &:hover svg,
    *:focus > & svg {
        height: ${({ iconSizeOnHover }) => iconSizeOnHover}px;
        width: ${({ iconSizeOnHover }) => iconSizeOnHover}px;
    }
    color: ${({ colorKey }) => variables[colorKey]};
    cursor: ${({ clickable }) => (clickable ? 'pointer' : 'auto')};
`;

export interface SimpleStatusIconProps {
    elementRef?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
    status?: IconStatus;
    height?: number;
    iconSize?: number;
    iconSizeOnHover?: number;
    onClick?: React.MouseEventHandler<
        HTMLButtonElement | HTMLAnchorElement
    > | null;
    onKeyDown?: React.KeyboardEventHandler<
        HTMLButtonElement | HTMLAnchorElement
    >;
}

const SimpleStatusIcon = ({
    status,
    height = 24,
    iconSize = 13,
    iconSizeOnHover = 15,
    onClick,
    onKeyDown,
    elementRef,
}: SimpleStatusIconProps) => {
    if (!status || !types.includes(status)) {
        return null;
    }

    const Icon = statusMap[status].icon;
    const { label } = statusMap[status];
    const { color } = statusMap[status];
    const clickable = typeof onClick === 'function';

    return clickable ? (
        <ClickableIcon
            onClick={onClick}
            onKeyDown={onKeyDown}
            tabIndex={0}
            elementRef={elementRef}
        >
            <IconContainer
                height={height}
                width={iconSizeOnHover}
                iconSize={iconSize}
                iconSizeOnHover={iconSizeOnHover}
                colorKey={color}
                clickable={clickable}
                ariaLabel={label}
            >
                <Icon />
            </IconContainer>
        </ClickableIcon>
    ) : (
        <IconContainer
            height={height}
            width={iconSizeOnHover}
            iconSize={iconSize}
            iconSizeOnHover={iconSize}
            colorKey={color}
            clickable={clickable}
            ariaLabel={label}
        >
            <Icon />
        </IconContainer>
    );
};

export default SimpleStatusIcon;
